/* eslint-disable react/forbid-prop-types */
import React, { useRef, useEffect, useMemo } from 'react';
import { useDraggable } from '@dnd-kit/core';
import {
  string, number, oneOfType, bool, func, shape, arrayOf, object
} from 'prop-types';
import { CabinetShape } from '../type';
import { Cabinet } from '../Cabinet';
import { originToScreen } from './handleDragAndDrop';

export const DraggableCabinet = React.memo(({
  cabinetId,
  left, top, width, height, itemId, mirror,
  designId, origin, spacer, spacerName, spacerMediaUrl,
  cabinets,
  border,
  anchorSide,
}) => {

  const getVoidSpacerData = (currCabinetId) => {

    let hasVoidSpacer = false;
    let voidSpacer = null;
    let convertedVoidSpacer = null;

    hasVoidSpacer = cabinets.some((cabinet) => cabinet.Sku === -3
        && cabinet.id === currCabinetId && cabinet.Origin.Width > 0);

    if (hasVoidSpacer) {
      voidSpacer = cabinets.find((cabinet) => cabinet.id === currCabinetId && cabinet.Sku === -3);

      convertedVoidSpacer = originToScreen(voidSpacer?.Origin);
    }

    return !hasVoidSpacer ? null : {
      id: voidSpacer.id,
      left: convertedVoidSpacer.Column,
      top: convertedVoidSpacer.Row,
      width: convertedVoidSpacer.Width,
      height: convertedVoidSpacer.Height,
      itemId: voidSpacer.Sku,
      name: voidSpacer.Name,
      origin: voidSpacer.Origin,
    };
  };

  const getDraggableData = () => {
    if (itemId === -3) {
      const parentCabinet = cabinets.find(
        (cabinet) => cabinet.id === cabinetId && cabinet.Sku !== -3
      );
      if (parentCabinet) {
        const { Row, Column, Width, Height } = originToScreen(parentCabinet.Origin);
        return {
          id: parentCabinet.id.toString(),
          data: {
            left: Column,
            top: Row,
            width: Width,
            height: Height,
            itemId: parentCabinet.Sku,
            anchorSide,
            voidSpacer: getVoidSpacerData(parentCabinet.id)
          }
        };
      }
      return {};
    }
    return {
      id: cabinetId.toString(),
      data: {
        left,
        top,
        width,
        height,
        itemId,
        anchorSide,
        voidSpacer: getVoidSpacerData(cabinetId)
      }
    };
  };
  const { attributes, listeners, setNodeRef, transform } = useDraggable(getDraggableData());

  // Compute style for dragging
  const draggableStyle = transform
    ? {
      transform: `translate(${transform.x}px, ${transform.y}px)`,
      touchAction: 'none',
    }
    : {};

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div ref={setNodeRef} style={draggableStyle}>
      <Cabinet
        id={cabinetId}
        top={top}
        left={left}
        width={width}
        height={height}
        itemId={itemId}
        mirror={mirror}
        designId={designId}
        origin={origin}
        spacer={spacer}
        spacerName={spacerName}
        spacerMediaUrl={spacerMediaUrl}
        cabinets={cabinets}
        dragListeners={listeners}
        dragAttributes={attributes}
        border={border}
      />
    </div>
  );

});

DraggableCabinet.propTypes = {
  cabinetId: string,
  left: oneOfType([string, number]),
  top: oneOfType([string, number]),
  width: oneOfType([string, number]),
  height: oneOfType([string, number]),
  itemId: oneOfType([string, number]).isRequired,
  mirror: bool,
  designId: string.isRequired,
  origin: shape({
    Row: number.isRequired,
    Column: number.isRequired,
    Height: number.isRequired,
    Width: number.isRequired,
  }).isRequired,
  spacer: bool,
  spacerName: string,
  spacerMediaUrl: string,
  cabinets: arrayOf(CabinetShape),
  border: bool,
  anchorSide: string
};

DraggableCabinet.defaultProps = {
  cabinetId: '',
  left: 0,
  top: 0,
  width: 0,
  height: 0,
  mirror: false,
  spacer: true,
  spacerName: '',
  spacerMediaUrl: '',
  cabinets: [],
  border: false,
  anchorSide: 'left'
};
